import { useNotification } from '../utils/notification/NotificationContext';
import { useTranslation } from 'react-i18next';
import {
  FondMutationTransactionInputDto,
  FondsMutation,
  useFondsMutation,
} from '../graphql/advisor/generates';
import { getGraphQLClient } from '../graphql/graphQLClient';
import { HttpStatusCode } from 'axios';
import { SetStateAction, useEffect, useState } from 'react';
import { useGetUserFonds } from './useGetUserFonds';
import { NotificationErrorData, NotificationSuccessData } from '../utils/notification/types';
import { TFunction } from 'i18next';

interface UpdateFondsError {
  response: {
    data: null;
    errors: {
      code: number;
      message: string;
    }[];
    status: number;
  };
}

export const getFondsUpdateOptions = ({
  setEtag,
  showError,
  showSuccess,
  t,
}: {
  setEtag: (value: SetStateAction<string | null | undefined>) => void;
  showError: (data?: NotificationErrorData) => void;
  showSuccess: (data: NotificationSuccessData) => void;
  t: TFunction<'advisor'>;
}) => ({
  onSuccess: (data: FondsMutation) => {
    const { etag: newEtag } = data.fond;
    if (newEtag) {
      setEtag(newEtag);
    }
    const failedTransaction = data.fond.transactionList.find(
      (transaction) => transaction.errorList?.length,
    );
    const errorMessage = failedTransaction?.errorList?.[0].message;
    if (errorMessage) {
      showError({ description: errorMessage });
      return;
    }
    showSuccess({ title: t('fundsModal.successMessage'), noTitle: false });
  },
  onError: (error: UpdateFondsError) => {
    if (error.response?.errors[0]?.code === HttpStatusCode.NotAcceptable) {
      showError({ description: t('toastNotifications.errorEtag') });
      return;
    }
    showError({ description: t('toastNotifications.error') });
  },
});

export const useUpdateFunds = () => {
  const { showSuccess, showError } = useNotification();
  const { t } = useTranslation();
  const getFonds = useGetUserFonds();
  const [etag, setEtag] = useState(getFonds.data?.etag);

  useEffect(() => {
    setEtag(getFonds.data?.etag);
  }, [getFonds.data?.etag]);

  const favoritesMutation = useFondsMutation(
    getGraphQLClient('advisor', etag),
    getFondsUpdateOptions({ t, setEtag, showError, showSuccess }),
  );

  return {
    ...favoritesMutation,
    mutateAsync: (transactionList: FondMutationTransactionInputDto[]) =>
      favoritesMutation
        .mutateAsync({
          fondsInput: {
            transactionList: transactionList.map((transaction) => ({
              operation: transaction.operation,
              data: {
                orderPreviousItem: transaction.data.orderPreviousItem,
                chartView: transaction.data.chartView,
                fondName: transaction.data.fondName,
                isin: transaction.data.isin,
                id: transaction.data.id,
              },
            })),
          },
        })
        .then((as) => as.fond.transactionList),
  };
};
